// The device report: what one recording on one browser says about the
// capture matrix in packages/speech/src/capture/README.md, as a row ready to
// paste into that table. Pure — the panel reads the globals and passes them in.
import type { CaptureMeta } from "@bindery/speech/contract";
import type { Capability } from "@bindery/speech/capture";

// The matrix's "Re-prompt after denial" column: whether tapping start again
// after denying showed the prompt. Only the tester can see a prompt, so this
// is what they observed, never something the page infers.
export type Reprompt = "untested" | "yes" | "no";

export interface BrowserGuess {
  // One of the matrix's row names where it can tell.
  readonly name: string;
  readonly version: string;
}

export interface DeviceReport {
  readonly userAgent: string;
  readonly browser: BrowserGuess | null;
  readonly capability: Capability;
  // From the most recent kept recording, or null before there is one.
  readonly recording: CaptureMeta | null;
}

const match = (ua: string, pattern: RegExp): string | null =>
  pattern.exec(ua)?.[1] ?? null;

// A guess from the user agent, in the order that matters: Chromebooks and
// Edge also say "Chrome", iOS browsers all say "Safari", and every Chromium
// says "Safari" too. iPadOS asks for desktop sites by default and reports
// itself as macOS, so an iPad reads as "Safari macOS" unless corrected.
export const guessBrowser = (ua: string): BrowserGuess | null => {
  const chrome = match(ua, /Chrome\/([\d.]+)/);
  if (ua.includes("CrOS") && chrome !== null)
    return { name: "Chromebook", version: chrome };
  const edge = match(ua, /Edg\/([\d.]+)/);
  if (edge !== null) return { name: "Edge", version: edge };
  const firefoxIos = match(ua, /FxiOS\/([\d.]+)/);
  if (firefoxIos !== null) return { name: "Firefox iOS", version: firefoxIos };
  const chromeIos = match(ua, /CriOS\/([\d.]+)/);
  if (chromeIos !== null) return { name: "Chrome iOS", version: chromeIos };
  const firefox = match(ua, /Firefox\/([\d.]+)/);
  if (firefox !== null) return { name: "Firefox", version: firefox };
  if (chrome !== null) return { name: "Chrome", version: chrome };
  const safari = match(ua, /Version\/([\d.]+).*Safari\//);
  if (safari !== null)
    return {
      name: /iPhone|iPad|iPod/.test(ua) ? "Safari iOS" : "Safari macOS",
      version: safari,
    };
  return null;
};

export const bytesPerMinute = (meta: CaptureMeta): number | null =>
  meta.durationMs > 0
    ? Math.round((meta.bytes * 60_000) / meta.durationMs)
    : null;

const cell = (text: string): string =>
  text.replace(/\|/g, "\\|").replace(/\s+/g, " ").trim();

// The row, in the matrix's column order: Browser | Version | Reported
// mimeType | Bytes per minute | Re-prompt after denial | Notes.
export const matrixRow = (
  report: DeviceReport,
  reprompt: Reprompt,
  notes: string,
): string => {
  const bpm =
    report.recording === null ? null : bytesPerMinute(report.recording);
  const cells = [
    report.browser?.name ?? "?",
    report.browser?.version ?? "?",
    report.recording === null ? "" : `\`${report.recording.mime}\``,
    bpm === null ? "" : bpm.toLocaleString("en-US"),
    reprompt === "untested" ? "" : reprompt,
    cell(notes),
  ];
  return `| ${cells.map(cell).join(" | ")} |`;
};

// The row plus the evidence behind it, for a commit message or a PR comment.
export const reportText = (
  report: DeviceReport,
  reprompt: Reprompt,
  notes: string,
): string => {
  const { capability, recording } = report;
  const lines = [
    matrixRow(report, reprompt, notes),
    "",
    `user agent: ${report.userAgent}`,
    capability.tag === "supported"
      ? `negotiated mime: ${capability.mime}`
      : `unsupported: ${capability.reason}`,
  ];
  if (recording !== null) {
    const bpm = bytesPerMinute(recording);
    lines.push(
      `recorder mimeType: ${recording.mime}`,
      `duration: ${String(recording.durationMs)} ms (ended by ${recording.endedBy})`,
      `bytes: ${String(recording.bytes)}`,
      `bytes per minute: ${bpm === null ? "n/a" : String(bpm)}`,
    );
  }
  return lines.join("\n");
};
